import clsx from 'clsx';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useDragScroll } from '@/hooks/useDragScroll';
import { useVacationCarousel } from '@/features/Vacations/hooks/useVacationCarousel';
import { ApprovalCard } from '../ApprovalCard';
import { CarouselArrow } from './components/CarouselArrow';
import { CarouselDots } from './components/CarouselDots';
import {
    CAROUSEL_LABELS,
    SKELETON_COUNT,
} from './approvalCarousel.definitions';
import type { ApprovalCarouselProps } from './approvalCarousel.types';

function CarouselSkeleton() {
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-busy="true">
            {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                <div key={index} className="h-80 animate-pulse rounded-2xl bg-gray-100" />
            ))}
        </div>
    );
}

function CarouselEmptyState({ message }: { message?: string }) {
    return (
        <div className="rounded-2xl border border-dashed border-primary-200 p-10 text-center">
            <CalendarDaysIcon
                className="mx-auto h-10 w-10 text-raro-gray-300"
                aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-raro-gray-700 font-montserrat">
                {CAROUSEL_LABELS.emptyTitle}
            </p>
            <p className="mt-1 text-sm text-raro-gray-500 font-montserrat">
                {message ?? CAROUSEL_LABELS.emptyDescription}
            </p>
        </div>
    );
}

export function ApprovalCarousel({
    items,
    onDecide,
    isLoading,
    emptyMessage,
}: ApprovalCarouselProps) {
    const {
        trackRef,
        page,
        pageCount,
        itemsPerView,
        canPrev,
        canNext,
        hasOverflow,
        next,
        prev,
        goToPage,
        handleKeyDown,
    } = useVacationCarousel({ itemCount: items.length });

    const { isDragging, dragProps } = useDragScroll<HTMLDivElement>(trackRef, hasOverflow);

    if (isLoading) return <CarouselSkeleton />;
    if (items.length === 0) return <CarouselEmptyState message={emptyMessage} />;

    const firstVisible = page * itemsPerView + 1;
    const lastVisible = Math.min(firstVisible + itemsPerView - 1, items.length);

    return (
        <section
            aria-roledescription="carrossel"
            aria-label={CAROUSEL_LABELS.region}
            onKeyDown={handleKeyDown}
            className="focus:outline-none"
        >
            <div
                ref={trackRef}
                tabIndex={0}
                {...dragProps}
                className={clsx(
                    '-mx-1 flex gap-4 overflow-x-auto px-1 pb-2 [scrollbar-width:none] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300 [&::-webkit-scrollbar]:hidden',
                    isDragging
                        ? 'cursor-grabbing select-none snap-none'
                        : 'snap-x snap-mandatory scroll-smooth',
                    hasOverflow && !isDragging && 'cursor-grab'
                )}
            >
                {items.map((item, index) => (
                    <div
                        key={`${item.kind}-${item.id}`}
                        className="w-full shrink-0 snap-start md:w-[calc((100%-1rem)/2)] xl:w-[calc((100%-2rem)/3)]"
                    >
                        <ApprovalCard
                            item={item}
                            onDecide={onDecide}
                            isActive={index >= firstVisible - 1 && index <= lastVisible - 1}
                        />
                    </div>
                ))}
            </div>

            {hasOverflow && (
                <div className="mt-4 flex items-center justify-end gap-3">
                    <CarouselDots pageCount={pageCount} page={page} onSelect={goToPage} />
                    <div className="flex items-center gap-2">
                        <CarouselArrow direction="prev" disabled={!canPrev} onClick={prev} />
                        <CarouselArrow direction="next" disabled={!canNext} onClick={next} />
                    </div>
                </div>
            )}
        </section>
    );
}

export default ApprovalCarousel;
